import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LocaleContext';
import QuoteShareActions from '../components/QuoteShareActions';
import Sheet from '../components/Sheet';
import { useServiceCatalog } from '../hooks/useServiceCatalog';
import {
  money,
  formatWhen,
  formatDate,
  displayName,
  vehicleTitle,
  statusLabel,
} from '../lib/format';
import { defaultBusinessProfile, fetchBusinessProfile } from '../lib/businessProfile';
import { buildQuotePdf, quoteFileName } from '../lib/quotePdf';
import { isQuoteShareActive } from '../lib/quoteShareUrl';
import { supabase } from '../lib/supabase';

const PAGE_SIZE = 15;

const emptyLine = () => ({
  key: crypto.randomUUID(),
  description: '',
  quantity: 1,
  unit_price: '',
});

function qtyOf(value) {
  const n = parseInt(value, 10);
  if (Number.isNaN(n) || n < 1) return 1;
  return n;
}

function lineTotal(line) {
  const unit = Number(line.unit_price);
  if (Number.isNaN(unit)) return 0;
  return qtyOf(line.quantity) * unit;
}

function bookingWhen(row) {
  return row?.scheduled_at || row?.created_at;
}

function bookingDayStamp(value) {
  if (!value) return '';
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return '';
  const month = `${at.getMonth() + 1}`.padStart(2, '0');
  const day = `${at.getDate()}`.padStart(2, '0');
  return `${at.getFullYear()}-${month}-${day}`;
}

function matchesBookingSearch(row, needle, dateFilter, extraHay = '') {
  if (dateFilter && bookingDayStamp(bookingWhen(row)) !== dateFilter) return false;
  if (!needle) return true;
  const digits = needle.replace(/\D/g, '');
  const phone = row?.profiles?.phone?.replace(/\D/g, '') || '';
  if (digits.length >= 3 && phone.includes(digits)) return true;
  const hay = [
    displayName(row?.profiles),
    row?.profiles?.phone,
    row?.profiles?.email,
    extraHay || row?.service_type,
    row?.vehicles?.plate_number,
    vehicleTitle(row?.vehicles),
    row?.branches?.name,
    formatWhen(bookingWhen(row)),
    bookingDayStamp(bookingWhen(row)),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(needle);
}

function quoteCustomer(quote, t) {
  if (quote?.bookings?.profiles) return displayName(quote.bookings.profiles);
  if (quote?.profiles) return displayName(quote.profiles);
  if (quote?.customer_name) return quote.customer_name;
  return t('finances.noCustomer');
}

function quoteVehicle(quote) {
  const booking = quote?.bookings || {};
  if (booking.vehicles) {
    return booking.vehicles.plate_number || vehicleTitle(booking.vehicles);
  }
  return quote?.vehicle_label || '';
}

function quoteTotal(quote) {
  return (quote?.items || []).reduce((sum, item) => sum + Number(item.price || 0), 0);
}

function statusCopy(status, t) {
  return (
    {
      sent: t('finances.statusSent'),
      approved: t('finances.statusApproved'),
      rejected: t('finances.statusRejected'),
      partial: t('finances.statusPartial'),
      draft: t('finances.statusDraft'),
    }[status] || status
  );
}

function initialsForName(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
}

export default function FinancesPage() {
  const t = useT();
  const { profile } = useAuth();
  const { serviceLabel, serviceSearchHay } = useServiceCatalog();
  const [params, setParams] = useSearchParams();
  const openId = params.get('open');
  const pageParam = Math.max(1, Number(params.get('page')) || 1);
  const creating = params.get('create') === '1';

  const [quotes, setQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [connectMode, setConnectMode] = useState('booking');
  const [bookingId, setBookingId] = useState('');
  const [bookingSearch, setBookingSearch] = useState('');
  const [bookingDate, setBookingDate] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerMatches, setCustomerMatches] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [vehicleLabel, setVehicleLabel] = useState('');
  const [note, setNote] = useState('');
  const [draft, setDraft] = useState([emptyLine()]);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [business, setBusiness] = useState(defaultBusinessProfile);
  const [exportingQuoteId, setExportingQuoteId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [formError, setFormError] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    const [{ data: quoteRows, error: quoteError }, { data: bookingRows, error: bookingError }] =
      await Promise.all([
        supabase
          .from('quotations')
          .select(
            'id, booking_id, customer_id, customer_name, customer_phone, vehicle_label, status, note, customer_note, quote_kind, share_token, share_revoked_at, share_expires_at, share_published_at, created_at, bookings:booking_id(id, service_type, scheduled_at, created_at, status, customer_id, vehicle_id, profiles:customer_id(first_name,last_name,full_name,phone,email), vehicles:vehicle_id(brand,model,year,plate_number), branches:branch_id(name,address,phone)), profiles:customer_id(first_name,last_name,full_name,phone,email), items:quotation_items(id, description, quantity, unit_price, price, decision)',
          )
          .order('created_at', { ascending: false }),
        supabase
          .from('bookings')
          .select(
            'id, service_type, scheduled_at, created_at, status, customer_id, vehicle_id, profiles:customer_id(first_name,last_name,full_name,phone,email), vehicles:vehicle_id(brand,model,year,plate_number), branches:branch_id(name)',
          )
          .not('status', 'in', '(cancelled,completed)')
          .order('created_at', { ascending: false })
          .limit(250),
      ]);
    setLoading(false);
    if (quoteError) {
      setError(quoteError.message);
      return;
    }
    if (bookingError) {
      setError(bookingError.message);
      return;
    }
    setQuotes(quoteRows || []);
    setBookings(bookingRows || []);
  }

  useEffect(() => {
    load();
    fetchBusinessProfile().then(setBusiness).catch(() => {});
  }, []);

  useEffect(() => {
    const needle = customerQuery.trim();
    if (connectMode !== 'standalone' || selectedCustomer || needle.length < 2) {
      setCustomerMatches([]);
      return undefined;
    }
    let alive = true;
    const timer = setTimeout(async () => {
      const safe = needle.replace(/[%,()]/g, '');
      const { data } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, full_name, phone, email')
        .eq('role', 'customer')
        .or(`full_name.ilike.%${safe}%,phone.ilike.%${safe}%,email.ilike.%${safe}%`)
        .limit(8);
      if (alive) setCustomerMatches(data || []);
    }, 280);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [customerQuery, connectMode, selectedCustomer]);

  const bookingOptions = useMemo(() => {
    const hasQuote = new Set(quotes.map((quote) => quote.booking_id).filter(Boolean));
    return bookings
      .filter(
        (row) =>
          !hasQuote.has(row.id) &&
          row.status !== 'cancelled' &&
          row.status !== 'completed',
      )
      .sort((a, b) => {
        const aAt = new Date(bookingWhen(a) || 0).getTime();
        const bAt = new Date(bookingWhen(b) || 0).getTime();
        return bAt - aAt;
      });
  }, [bookings, quotes]);

  const filteredBookingOptions = useMemo(() => {
    const needle = bookingSearch.trim().toLowerCase();
    return bookingOptions.filter((row) =>
      matchesBookingSearch(row, needle, bookingDate, serviceSearchHay(row.service_type)),
    );
  }, [bookingOptions, bookingSearch, bookingDate, serviceSearchHay]);

  const selectedBooking = useMemo(
    () => bookingOptions.find((row) => row.id === bookingId) || null,
    [bookingOptions, bookingId],
  );

  const filteredQuotes = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return quotes.filter((quote) => {
      if (statusFilter !== 'all' && quote.status !== statusFilter) return false;
      if (!needle) return true;
      const booking = quote.bookings || {};
      const hay = [
        booking.service_type,
        serviceSearchHay(booking.service_type),
        booking.branches?.name,
        booking.vehicles?.plate_number,
        vehicleTitle(booking.vehicles),
        displayName(booking.profiles),
        displayName(quote.profiles),
        quote.customer_name,
        quote.customer_phone,
        quote.vehicle_label,
        quote.id,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [quotes, query, statusFilter, serviceSearchHay]);

  const pageCount = Math.max(1, Math.ceil(filteredQuotes.length / PAGE_SIZE));
  const page = Math.min(pageParam, pageCount);
  const pageRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredQuotes.slice(start, start + PAGE_SIZE);
  }, [filteredQuotes, page]);
  const rangeStart = filteredQuotes.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, filteredQuotes.length);

  const openQuote = quotes.find((row) => row.id === openId) || null;

  const draftTotal = useMemo(
    () => draft.reduce((sum, line) => sum + lineTotal(line), 0),
    [draft],
  );

  function setPage(nextPage) {
    const safe = Math.min(Math.max(1, nextPage), pageCount);
    const next = new URLSearchParams(params);
    if (safe <= 1) next.delete('page');
    else next.set('page', String(safe));
    setParams(next, { replace: true });
  }

  function onSearchChange(value) {
    setQuery(value);
    if (params.get('page')) {
      const next = new URLSearchParams(params);
      next.delete('page');
      setParams(next, { replace: true });
    }
  }

  function openCreate() {
    const next = new URLSearchParams(params);
    next.delete('open');
    next.set('create', '1');
    setParams(next, { replace: true });
    setFormError('');
    setOk('');
  }

  function closeCreate() {
    const next = new URLSearchParams(params);
    next.delete('create');
    setParams(next, { replace: true });
    setFormError('');
  }

  function openQuoteSheet(id) {
    const next = new URLSearchParams(params);
    next.delete('create');
    next.set('open', id);
    setParams(next, { replace: true });
    setOk('');
  }

  function closeQuoteSheet() {
    const next = new URLSearchParams(params);
    next.delete('open');
    setParams(next, { replace: true });
  }

  function setLine(key, patch) {
    setDraft((current) =>
      current.map((line) => {
        if (line.key !== key) return line;
        const next = { ...line, ...patch };
        if (patch.quantity !== undefined) next.quantity = qtyOf(patch.quantity);
        return next;
      }),
    );
  }

  function resetForm() {
    setBookingId('');
    setBookingSearch('');
    setBookingDate('');
    setCustomerQuery('');
    setCustomerMatches([]);
    setSelectedCustomer(null);
    setVehicleLabel('');
    setNote('');
    setDraft([emptyLine()]);
    setConnectMode('booking');
  }

  function switchMode(mode) {
    setConnectMode(mode);
    setFormError('');
    if (mode === 'booking') {
      setSelectedCustomer(null);
      setCustomerQuery('');
      setVehicleLabel('');
    } else {
      setBookingId('');
      setBookingSearch('');
      setBookingDate('');
    }
  }

  async function createQuote(event) {
    event.preventDefault();
    const items = draft
      .map((line) => ({
        description: line.description.trim(),
        quantity: qtyOf(line.quantity),
        unit_price: Number(line.unit_price),
      }))
      .filter((line) => line.description);
    if (items.length === 0) {
      setFormError(t('finances.needOneLine'));
      return;
    }
    if (
      items.some(
        (line) => line.quantity < 1 || line.unit_price < 0 || Number.isNaN(line.unit_price),
      )
    ) {
      setFormError(t('finances.needLines'));
      return;
    }
    if (connectMode === 'booking' && !bookingId) {
      setFormError(t('finances.needBooking'));
      return;
    }

    setSaving(true);
    setFormError('');
    setOk('');

    let rpcError;
    if (connectMode === 'booking') {
      ({ error: rpcError } = await supabase.rpc('send_booking_quotation', {
        p_booking_id: bookingId,
        p_items: items,
        p_note: note.trim() || null,
      }));
    } else {
      ({ error: rpcError } = await supabase.rpc('create_standalone_quotation', {
        p_items: items,
        p_note: note.trim() || null,
        p_customer_id: selectedCustomer?.id || null,
        p_customer_name: selectedCustomer ? displayName(selectedCustomer) : null,
        p_customer_phone: selectedCustomer?.phone || null,
        p_vehicle_label: vehicleLabel.trim() || null,
        p_branch_id: profile?.branch_id || null,
        p_quote_kind: 'repair',
      }));
    }

    setSaving(false);
    if (rpcError) {
      setFormError(rpcError.message);
      return;
    }

    setOk(
      connectMode === 'booking' || selectedCustomer
        ? t('finances.created')
        : t('finances.createdStandalone'),
    );
    resetForm();
    closeCreate();
    load();
  }

  async function exportQuote(quote) {
    const booking = quote.bookings || {
      profiles: quote.profiles || {
        full_name: quote.customer_name,
        phone: quote.customer_phone,
      },
      vehicles: quote.vehicle_label ? { brand: quote.vehicle_label } : null,
    };
    setExportingQuoteId(quote.id);
    setError('');
    setOk('');
    try {
      const doc = await buildQuotePdf(quote, booking, business, {
        quoteKind: quote.quote_kind,
      });
      doc.save(quoteFileName(quote.booking_id || quote.id, quote.quote_kind));
      setOk(t('finances.exported'));
    } catch (e) {
      setError(e?.message || 'Could not export quotation PDF.');
    } finally {
      setExportingQuoteId('');
    }
  }

  async function shareQuote(quote) {
    const booking = quote.bookings || {
      profiles: quote.profiles || {
        full_name: quote.customer_name,
        phone: quote.customer_phone,
      },
      vehicles: quote.vehicle_label ? { brand: quote.vehicle_label } : null,
      service_type: quote.vehicle_label || 'quotation',
    };
    setExportingQuoteId(quote.id);
    setError('');
    setOk('');
    try {
      const doc = await buildQuotePdf(quote, booking, business, {
        quoteKind: quote.quote_kind,
      });
      const blob = doc.output('blob');
      const file = new File(
        [blob],
        quoteFileName(quote.booking_id || quote.id, quote.quote_kind),
        { type: 'application/pdf' },
      );
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: `Quotation ${quote.booking_id || quote.id}`,
          text: `Quotation for ${booking.service_type || 'visit'}`,
          files: [file],
        });
        setOk(t('finances.shared'));
      } else {
        doc.save(quoteFileName(quote.booking_id || quote.id, quote.quote_kind));
        setOk(t('finances.sharedFallback'));
      }
    } catch (e) {
      if (e?.name !== 'AbortError') setError(e?.message || 'Could not share quotation.');
    } finally {
      setExportingQuoteId('');
    }
  }

  return (
    <section className="customers-page finances-page">
      <header className="page-head customers-head finances-head">
        <div>
          <span className="kicker">{t('finances.kicker')}</span>
          <h1>{t('finances.title')}</h1>
          <p className="lede">{t('finances.lede')}</p>
        </div>
        <div className="finances-head-tools">
          <label className="customers-search">
            <span className="material-symbol" aria-hidden="true">
              search
            </span>
            <input
              className="search"
              placeholder={t('finances.listSearch')}
              value={query}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </label>
          <select
            className="finances-status-filter"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              if (params.get('page')) {
                const next = new URLSearchParams(params);
                next.delete('page');
                setParams(next, { replace: true });
              }
            }}
          >
            <option value="all">{t('finances.allStatuses')}</option>
            <option value="sent">{t('finances.statusSent')}</option>
            <option value="approved">{t('finances.statusApproved')}</option>
            <option value="partial">{t('finances.statusPartial')}</option>
            <option value="rejected">{t('finances.statusRejected')}</option>
            <option value="draft">{t('finances.statusDraft')}</option>
          </select>
          <button type="button" className="primary" onClick={openCreate}>
            <span className="material-symbol">add</span>
            {t('finances.create')}
          </button>
        </div>
      </header>

      {error && <p className="form-error">{error}</p>}
      {ok && !creating && <p className="form-ok">{ok}</p>}

      {loading ? (
        <p className="empty">{t('finances.loading')}</p>
      ) : filteredQuotes.length === 0 ? (
        <p className="empty">{t('finances.none')}</p>
      ) : (
        <>
          <div className="customers-toolbar">
            <p className="customers-count">
              {t('customers.showing', {
                from: rangeStart,
                to: rangeEnd,
                total: filteredQuotes.length,
              })}
            </p>
            {query.trim() || statusFilter !== 'all' ? (
              <button
                type="button"
                className="text-link"
                onClick={() => {
                  onSearchChange('');
                  setStatusFilter('all');
                }}
              >
                {t('customers.clearSearch')}
              </button>
            ) : null}
          </div>

          <div className="customers-directory finances-directory">
            <div className="customers-table-head finances-table-head" aria-hidden="true">
              <span>{t('finances.colQuote')}</span>
              <span>{t('finances.colCustomer')}</span>
              <span>{t('finances.colStatus')}</span>
              <span>{t('finances.colTotal')}</span>
              <span />
            </div>
            {pageRows.map((quote) => {
              const booking = quote.bookings || {};
              const customer = quoteCustomer(quote, t);
              const vehicle = quoteVehicle(quote);
              const visitLabel = quote.booking_id
                ? serviceLabel(booking.service_type, 'common.visit')
                : t('finances.standaloneBadge');
              const when = booking.scheduled_at || booking.created_at || quote.created_at;
              return (
                <button
                  key={quote.id}
                  type="button"
                  className="customer-row finances-row"
                  onClick={() => openQuoteSheet(quote.id)}
                >
                  <div className="customer-row-person">
                    <span className="customer-row-avatar" aria-hidden="true">
                      {initialsForName(customer)}
                    </span>
                    <div className="finances-row-main">
                      <span className="customer-row-name">{visitLabel}</span>
                      <span className="finances-row-sub">
                        {[when ? formatDate(when) : null, vehicle].filter(Boolean).join(' · ') ||
                          '—'}
                      </span>
                    </div>
                  </div>
                  <div className="customer-row-contact">
                    <span>{customer}</span>
                    <span>
                      {booking.profiles?.phone ||
                        quote.profiles?.phone ||
                        quote.customer_phone ||
                        '—'}
                    </span>
                  </div>
                  <div className="finances-row-status">
                    <em className={`pill status-${quote.status}`}>
                      {statusCopy(quote.status, t)}
                    </em>
                    {isQuoteShareActive(quote) ? (
                      <span className="finances-link-live">{t('finances.linkLive')}</span>
                    ) : null}
                  </div>
                  <div className="finances-row-total">{money(quoteTotal(quote), 2)}</div>
                  <span className="material-symbol customer-row-chevron">chevron_right</span>
                </button>
              );
            })}
          </div>

          {pageCount > 1 ? (
            <nav className="customers-pager" aria-label={t('finances.pagination')}>
              <button
                type="button"
                className="customers-pager-btn"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                <span className="material-symbol">chevron_left</span>
                {t('customers.prev')}
              </button>
              <p className="customers-pager-status">
                {t('customers.pageOf', { page, pages: pageCount })}
              </p>
              <button
                type="button"
                className="customers-pager-btn"
                disabled={page >= pageCount}
                onClick={() => setPage(page + 1)}
              >
                {t('customers.next')}
                <span className="material-symbol">chevron_right</span>
              </button>
            </nav>
          ) : null}
        </>
      )}

      {creating ? (
        <Sheet
          variant="modal"
          wide
          className="sheet-quote-create"
          bodyClassName="sheet-body-quote-create"
          title={t('finances.create')}
          subtitle={t('finances.createSubtitle')}
          onClose={closeCreate}
        >
          {formError ? <p className="form-error">{formError}</p> : null}
          <form className="quote-compose quote-compose-modal" onSubmit={createQuote}>
            <div className="view-tabs finances-connect-tabs" role="tablist">
              <button
                type="button"
                className={connectMode === 'booking' ? 'view-tab on' : 'view-tab'}
                onClick={() => switchMode('booking')}
              >
                {t('finances.modeBooking')}
              </button>
              <button
                type="button"
                className={connectMode === 'standalone' ? 'view-tab on' : 'view-tab'}
                onClick={() => switchMode('standalone')}
              >
                {t('finances.modeStandalone')}
              </button>
            </div>

            {connectMode === 'booking' ? (
              <div className="booking-picker booking-picker-compact">
                <div className="booking-picker-filters">
                  <input
                    className="search"
                    placeholder={t('finances.searchPlaceholder')}
                    value={bookingSearch}
                    onChange={(e) => setBookingSearch(e.target.value)}
                  />
                  <input
                    className="search"
                    type="date"
                    aria-label={t('finances.bookingDate')}
                    value={bookingDate}
                    onChange={(e) => setBookingDate(e.target.value)}
                  />
                </div>
                <p className="booking-picker-hint">
                  {filteredBookingOptions.length === 1
                    ? t('finances.bookingCountOne')
                    : t('finances.bookingCount', { n: filteredBookingOptions.length })}
                </p>
                <ul className="booking-picker-list" role="listbox">
                  {filteredBookingOptions.length === 0 ? (
                    <li className="booking-picker-empty">{t('finances.noMatch')}</li>
                  ) : (
                    filteredBookingOptions.map((row) => {
                      const selected = bookingId === row.id;
                      return (
                        <li key={row.id}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={selected}
                            className={
                              selected ? 'booking-picker-item on' : 'booking-picker-item'
                            }
                            onClick={() => setBookingId(row.id)}
                          >
                            <strong>
                              {serviceLabel(row.service_type, 'common.visit')}
                              {' · '}
                              {displayName(row.profiles)}
                            </strong>
                            <span>
                              {formatWhen(bookingWhen(row))}
                              {row.profiles?.phone ? ` · ${row.profiles.phone}` : ''}
                              {' · '}
                              {row.vehicles?.plate_number || vehicleTitle(row.vehicles)}
                              {' · '}
                              {statusLabel(row.status)}
                            </span>
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>
                {selectedBooking ? (
                  <p className="form-ok booking-picker-selected">
                    {t('finances.selected', {
                      service: serviceLabel(selectedBooking.service_type, 'common.visit'),
                      when: formatWhen(bookingWhen(selectedBooking)),
                    })}
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="booking-picker standalone-picker">
                <span className="booking-picker-label">{t('finances.standaloneCustomer')}</span>
                <p className="booking-picker-hint">{t('finances.standaloneHint')}</p>
                {selectedCustomer ? (
                  <div className="standalone-selected">
                    <div>
                      <strong>{displayName(selectedCustomer)}</strong>
                      <span>
                        {[selectedCustomer.phone, selectedCustomer.email]
                          .filter(Boolean)
                          .join(' · ') || t('customer.noPhone')}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="ghost"
                      onClick={() => {
                        setSelectedCustomer(null);
                        setCustomerQuery('');
                      }}
                    >
                      {t('finances.clearCustomer')}
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      className="search"
                      placeholder={t('finances.customerSearch')}
                      value={customerQuery}
                      onChange={(e) => setCustomerQuery(e.target.value)}
                    />
                    {customerMatches.length > 0 ? (
                      <ul className="booking-picker-list" role="listbox">
                        {customerMatches.map((row) => (
                          <li key={row.id}>
                            <button
                              type="button"
                              className="booking-picker-item"
                              onClick={() => {
                                setSelectedCustomer(row);
                                setCustomerMatches([]);
                                setCustomerQuery('');
                              }}
                            >
                              <strong>{displayName(row)}</strong>
                              <span>
                                {[row.phone, row.email].filter(Boolean).join(' · ') || '—'}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    <p className="booking-picker-hint">{t('finances.noCustomerOk')}</p>
                  </>
                )}
                <label>
                  {t('finances.vehicleLabel')}
                  <input
                    value={vehicleLabel}
                    onChange={(e) => setVehicleLabel(e.target.value)}
                    placeholder={t('finances.vehicleLabelPlaceholder')}
                  />
                </label>
              </div>
            )}

            <div className="quote-table" role="table">
              <div className="quote-row head" role="row">
                <span>Item</span>
                <span>Qty</span>
                <span>Unit price</span>
                <span>Line</span>
                <span />
              </div>
              {draft.map((line) => (
                <div className="quote-row" role="row" key={line.key}>
                  <input
                    aria-label="Item"
                    placeholder="Brake pads, labour, oil…"
                    value={line.description}
                    onChange={(e) => setLine(line.key, { description: e.target.value })}
                  />
                  <div className="qty">
                    <button
                      type="button"
                      aria-label="Decrease quantity"
                      disabled={qtyOf(line.quantity) <= 1}
                      onClick={() => setLine(line.key, { quantity: qtyOf(line.quantity) - 1 })}
                    >
                      −
                    </button>
                    <input
                      aria-label="Quantity"
                      inputMode="numeric"
                      value={line.quantity}
                      onChange={(e) => setLine(line.key, { quantity: e.target.value })}
                      onBlur={() => setLine(line.key, { quantity: qtyOf(line.quantity) })}
                    />
                    <button
                      type="button"
                      aria-label="Increase quantity"
                      onClick={() => setLine(line.key, { quantity: qtyOf(line.quantity) + 1 })}
                    >
                      +
                    </button>
                  </div>
                  <label className="unit">
                    <span>EGP</span>
                    <input
                      aria-label="Unit price"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={line.unit_price}
                      onChange={(e) => setLine(line.key, { unit_price: e.target.value })}
                    />
                  </label>
                  <strong className="line-sum">{money(lineTotal(line), 2)}</strong>
                  <button
                    type="button"
                    className="icon-kill"
                    aria-label="Remove line"
                    disabled={draft.length === 1}
                    onClick={() =>
                      setDraft((current) => current.filter((item) => item.key !== line.key))
                    }
                  >
                    <span className="material-symbol">close</span>
                  </button>
                </div>
              ))}
            </div>
            <div className="quote-toolbar">
              <button
                type="button"
                className="ghost"
                onClick={() => setDraft((current) => [...current, emptyLine()])}
              >
                Add line
              </button>
              <div className="quote-total">
                <span>Total</span>
                <strong>{money(draftTotal, 2)}</strong>
              </div>
            </div>
            <label>
              Note to customer
              <textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Labour includes fitting and diagnostics."
              />
            </label>
            <button type="submit" className="primary dispatch-send" disabled={saving}>
              {saving ? t('finances.sending') : t('finances.create')}
            </button>
          </form>
        </Sheet>
      ) : null}

      {openQuote ? (
        <Sheet
          variant="modal"
          wide
          className="sheet-quote-detail"
          bodyClassName="sheet-body-quote-detail"
          title={
            openQuote.booking_id
              ? serviceLabel(openQuote.bookings?.service_type, 'common.visit')
              : t('finances.standaloneBadge')
          }
          subtitle={quoteCustomer(openQuote, t)}
          onClose={closeQuoteSheet}
        >
          <div className="quote-detail">
            <div className="quote-detail-meta">
              <em className={`pill status-${openQuote.status}`}>
                {statusCopy(openQuote.status, t)}
              </em>
              <strong>{money(quoteTotal(openQuote), 2)}</strong>
            </div>
            <dl className="quote-detail-facts">
              <div>
                <dt>{t('finances.colCustomer')}</dt>
                <dd>{quoteCustomer(openQuote, t)}</dd>
              </div>
              <div>
                <dt>{t('finances.vehicleLabel')}</dt>
                <dd>{quoteVehicle(openQuote) || '—'}</dd>
              </div>
              <div>
                <dt>{t('finances.createdAt')}</dt>
                <dd>{formatWhen(openQuote.created_at)}</dd>
              </div>
              {openQuote.booking_id ? (
                <div>
                  <dt>{t('finances.visit')}</dt>
                  <dd>
                    <Link to={`/bookings/${openQuote.booking_id}`}>
                      {t('finances.openVisit')}
                    </Link>
                  </dd>
                </div>
              ) : null}
            </dl>

            {openQuote.note ? (
              <p className="quote-detail-note">{openQuote.note}</p>
            ) : null}

            <ul className="quote-detail-lines">
              {(openQuote.items || []).map((item) => (
                <li key={item.id}>
                  <div>
                    <strong>{item.description}</strong>
                    <span>
                      {item.quantity} × {money(item.unit_price, 2)}
                    </span>
                  </div>
                  <b>{money(item.price, 2)}</b>
                </li>
              ))}
            </ul>

            <div className="quote-detail-actions">
              <button
                type="button"
                className="ghost"
                disabled={exportingQuoteId === openQuote.id}
                onClick={() => exportQuote(openQuote)}
              >
                Export PDF
              </button>
              <button
                type="button"
                className="ghost"
                disabled={exportingQuoteId === openQuote.id}
                onClick={() => shareQuote(openQuote)}
              >
                Share PDF
              </button>
            </div>
            <QuoteShareActions
              quote={openQuote}
              onRefresh={(next) =>
                setQuotes((rows) =>
                  rows.map((row) => (row.id === openQuote.id ? { ...row, ...next } : row)),
                )
              }
            />
          </div>
        </Sheet>
      ) : null}
    </section>
  );
}
